import z from "zod";
import createEnv from "@/common/env/createEnv";

const { env, getEnv, getEnvOrFail } = createEnv({
  server: {
    // Server environment variables
    COHESION_SOURCE_KEY: z.string().optional(),
    COHESION_WRITE_KEY: z.string().optional(),
    SMART_SEARCH_API_KEY: z.string().optional(),
    SMART_SEARCH_ENDPOINT: z.string().optional(),
    LOG_LEVEL: z
      .enum(["debug", "info", "warn", "error", "trace", "fatal"])
      .optional()
      .default("info"),
    DISABLE_SAFELIST: z.enum(["true", "false"]).optional().default("false"),
    FAUST_SECRET_KEY: z.string().optional(),
    CONTENTFUL_SPACE_ID: z.string().optional(),
    CONTENTFUL_DELIVERY_API_KEY: z.string().optional(),
    CONTENTFUL_PREVIEW_API_KEY: z.string().optional(),
    SEGMENT_WRITE_KEY: z.string().optional(),
    PRETTY_PRINT_LOGS: z.enum(["true", "false"]).optional().default("false"),
    DISABLE_ROBOTS: z.enum(["true", "false"]).optional(),
    EXTERNAL_PARTNER_API_BASE_URL: z.string().url().optional(),
    COOKIE_DOMAIN: z.string().optional(),
    OAUTH_ID: z.string().optional(),
    OAUTH_SECRET: z.string().optional(),
    SECURE_PREVIEW_USERNAME: z.string().optional(),
    SECURE_PREVIEW_PASSWORD: z.string().optional(),
    ENABLE_BOT_BLOCKING: z.enum(["true", "false"]).optional(),
    ONE_TRUST_SCRIPT_ID: z.string().optional(),
    GTM_CONTAINER_ID: z.string().optional(),

    // Marketplace - PRODUCT API
    PRODUCT_API_BASE_URL: z.string().url().optional(),
    PRODUCT_API_KEY: z.string().optional(),
    PRODUCT_API_MULTI_DEV_ID: z.string().optional(),

    // Mobius - RV Inventory
    MOBIUS_API_BASE_URL: z.string().url().optional(),
    MOBIUS_API_KEY: z.string().optional(),

    // Ecommerce - Commerce Tools
    ECOMMERCE_COMMERCETOOLS_KEY: z.string().optional(),
    ECOMMERCE_COMMERCETOOLS_SECRET: z.string().optional(),
    ECOMMERCE_COMMERCETOOLS_API_BASE: z.string().url().optional(),
    ECOMMERCE_COMMERCETOOLS_AUTH_BASE: z.string().url().optional(),
    ECOMMERCE_COMMERCETOOLS_PROJECT: z.string().optional(),
    ECOMMERCE_COMMERCETOOLS_SCOPES: z.string().optional(),

    // Proxy base URLs
    PROXY_OFFERINGS_SERVICE: z.string().url().optional(),

    // Tenant config
    TENANT_CONFIG_API_BASE_URL: z.string().url().optional(),

    // FX Quote API
    FX_QUOTE_API_BASE_URL: z.string().url().optional(),
    FX_QUOTE_API_KEY: z.string().optional(),

    // Redirect Service
    USE_REDIRECT_SERVICE: z.enum(["true", "false"]).optional().default("false"),
    REDIRECT_SERVICE_API: z.string().url().optional(),
    REDIRECT_HOST: z.string().optional().default("edx.org"),
  },
  client: {
    // Client environment variables
    NEXT_PUBLIC_SITE_URL: z.string().url(),
    NEXT_PUBLIC_COHESION_CONSENT_REQUIRED: z.enum(["true", "false"]).optional(),
    NEXT_PUBLIC_WORDPRESS_URL: z.string().url(),
    NEXT_PUBLIC_APP_VERSION: z.string(),
    NEXT_PUBLIC_ALGOLIA_PRODUCT_INDEX: z.string().optional().default("product"),
    NEXT_PUBLIC_ALGOLIA_PRODUCT_SUMMARY_INDEX: z
      .string()
      .optional()
      .default("rv_product_summary"),
    NEXT_PUBLIC_DISABLE_PERSONALIZED_EXPERIENCE: z
      .enum(["true", "false"])
      .optional(),
    NEXT_PUBLIC_COMMERCE_COORDINATOR_BASE_URL: z.string().url().optional(),
    // Feature flags
    NEXT_PUBLIC_FEATURE_STREAMLINE_CHECKOUT: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_DEGREE_DETAIL_REDESIGN_TRAFFIC_V5_2: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_DEGREE_DETAIL_V51_HERO_STATS: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_PRODUCT_SUMMARY_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_INSTRUCTOR_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_COURSE_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_PARTNER_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_PROGRAM_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_MOBILE_BANNER_KEY: z.string().optional(),
    NEXT_PUBLIC_FEATURE_SITEMAP_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_PRODUCT_VIEW_TRACKING: z
      .enum(["legacy", "test"])
      .optional(),
    // Authorizer, ie track selection
    NEXT_PUBLIC_AUTH_DOMAIN: z.string().optional(),
    NEXT_PUBLIC_FEATURE_REGISTER_NEXT_URL_ENABLED: z
      .enum(["true", "false"])
      .optional(),
    NEXT_PUBLIC_FEATURE_ADD_TO_CART_LINK: z
      .enum(["enabled", "disabled"])
      .optional(),
    // Login Auth
    NEXT_PUBLIC_LMS_BASE_URL: z.string().url(),
    NEXT_PUBLIC_ACCESS_TOKEN_COOKIE_NAME: z.string(),
    NEXT_PUBLIC_SIGNATURE_COOKIE_NAME: z.string(),
    NEXT_PUBLIC_USER_INFO_COOKIE_NAME: z.string(),
    NEXT_PUBLIC_CAREERS_BASE_URL: z.string().url(),
    NEXT_PUBLIC_HOME_BASE_URL: z.string().url(),
    NEXT_PUBLIC_FEATURE_ENABLE_LOGIN: z.enum(["true", "false"]).optional(),
    NEXT_PUBLIC_AUTHN_BASE_URL: z.string().url(),
    NEXT_PUBLIC_MARKETING_EMAILS_OPT_IN: z.enum(["true", "false"]).optional(),
    NEXT_PUBLIC_FEATURE_DEGREE_TY_PAGE_ENABLED: z
      .enum(["true", "false"])
      .optional()
      .default("false"),
    NEXT_PUBLIC_FEATURE_SEARCH_BAR_REVAMP_TEST: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_DEGREE_TUITION_MODULE: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_NAVIGATION_MODAL_TEST: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_ABANDONED_CART_TEST: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_MULTI_PURCHASE: z.enum(["legacy", "test"]).optional(),
    NEXT_PUBLIC_FEATURE_LOB_ENTRY_POINTS: z.enum(["legacy", "test"]).optional(),
    NEXT_PUBLIC_FEATURE_STATS_SOCIAL_PROOF_TEST: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_OC_COUNTDOWN_BANNER: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_CYT_MOBILE_FIRST_REDESIGN: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_GTM_AUTOMATION_MODULE: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_DEGREE_HUB_REDESIGN: z
      .enum(["legacy", "control"])
      .optional(),
    NEXT_PUBLIC_FEATURE_REFERRAL_TEMPLATE: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_AUTHX: z.enum(["enable", "disable"]).optional(),
    NEXT_PUBLIC_DYNAMIC_FLOW_API: z.string().url().optional(),
    // Dynamic Experience Tenant ID (from EDU Direct Admin)
    NEXT_PUBLIC_AUTHX_TENANT_ID: z.string().optional(),
    NEXT_PUBLIC_FEATURE_AUTHX_ENTERPRISE_REDIRECT: z
      .enum(["enable", "disable"])
      .optional(),
    NEXT_PUBLIC_CHECKOUT_BASE_URL: z.string().url(),
    // reCAPTCHA v3
    NEXT_PUBLIC_RECAPTCHA_SITE_KEY: z.string().optional(),
    // Monarch auto request
    NEXT_PUBLIC_MONARCH_AUTO_REQUEST_ENABLED: z
      .enum(["true", "false"])
      .optional(),
    //dapi_random_id
    NEXT_PUBLIC_DAPI_COOKIE_NAME: z.string().optional(),
    NEXT_PUBLIC_FEATURE_XML_FEEDS_ENABLED: z.enum(["true", "false"]).optional(),
    NEXT_PUBLIC_FEATURE_SUPERLATIVE_PRODUCT_CARDS: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_SUBSCRIPTIONS: z.enum(["legacy", "test"]).optional(),
    NEXT_PUBLIC_FEATURE_HOMEPAGE_TRENDING_COURSE_UPDATE: z
      .enum(["legacy", "test"])
      .optional(),
  },
  runtimeEnv: {
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_COHESION_CONSENT_REQUIRED:
      process.env.NEXT_PUBLIC_COHESION_CONSENT_REQUIRED,
    NEXT_PUBLIC_WORDPRESS_URL: process.env.NEXT_PUBLIC_WORDPRESS_URL,
    NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
    NEXT_PUBLIC_ALGOLIA_PRODUCT_INDEX:
      process.env.NEXT_PUBLIC_ALGOLIA_PRODUCT_INDEX,
    NEXT_PUBLIC_ALGOLIA_PRODUCT_SUMMARY_INDEX:
      process.env.NEXT_PUBLIC_ALGOLIA_PRODUCT_SUMMARY_INDEX,
    NEXT_PUBLIC_DISABLE_PERSONALIZED_EXPERIENCE:
      process.env.NEXT_PUBLIC_DISABLE_PERSONALIZED_EXPERIENCE,
    NEXT_PUBLIC_COMMERCE_COORDINATOR_BASE_URL:
      process.env.NEXT_PUBLIC_COMMERCE_COORDINATOR_BASE_URL,
    NEXT_PUBLIC_FEATURE_STREAMLINE_CHECKOUT:
      process.env.NEXT_PUBLIC_FEATURE_STREAMLINE_CHECKOUT,
    NEXT_PUBLIC_FEATURE_DEGREE_DETAIL_V51_HERO_STATS:
      process.env.NEXT_PUBLIC_FEATURE_DEGREE_DETAIL_V51_HERO_STATS,
    NEXT_PUBLIC_FEATURE_PRODUCT_SUMMARY_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_PRODUCT_SUMMARY_SOURCE,
    NEXT_PUBLIC_FEATURE_INSTRUCTOR_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_INSTRUCTOR_SOURCE,
    NEXT_PUBLIC_FEATURE_COURSE_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_COURSE_SOURCE,
    NEXT_PUBLIC_MOBILE_BANNER_KEY: process.env.NEXT_PUBLIC_MOBILE_BANNER_KEY,
    NEXT_PUBLIC_FEATURE_SITEMAP_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_SITEMAP_SOURCE,
    NEXT_PUBLIC_FEATURE_PARTNER_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_PARTNER_SOURCE,
    NEXT_PUBLIC_FEATURE_PROGRAM_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_PROGRAM_SOURCE,
    NEXT_PUBLIC_AUTH_DOMAIN: process.env.NEXT_PUBLIC_AUTH_DOMAIN,
    NEXT_PUBLIC_FEATURE_REGISTER_NEXT_URL_ENABLED:
      process.env.NEXT_PUBLIC_FEATURE_REGISTER_NEXT_URL_ENABLED,
    NEXT_PUBLIC_FEATURE_ADD_TO_CART_LINK:
      process.env.NEXT_PUBLIC_FEATURE_ADD_TO_CART_LINK,
    NEXT_PUBLIC_FEATURE_PRODUCT_VIEW_TRACKING:
      process.env.NEXT_PUBLIC_FEATURE_PRODUCT_VIEW_TRACKING,
    // Auth
    NEXT_PUBLIC_LMS_BASE_URL: process.env.NEXT_PUBLIC_LMS_BASE_URL,
    NEXT_PUBLIC_ACCESS_TOKEN_COOKIE_NAME:
      process.env.NEXT_PUBLIC_ACCESS_TOKEN_COOKIE_NAME,
    NEXT_PUBLIC_SIGNATURE_COOKIE_NAME:
      process.env.NEXT_PUBLIC_SIGNATURE_COOKIE_NAME,
    NEXT_PUBLIC_USER_INFO_COOKIE_NAME:
      process.env.NEXT_PUBLIC_USER_INFO_COOKIE_NAME,
    NEXT_PUBLIC_CAREERS_BASE_URL:
      process.env.NEXT_PUBLIC_CAREERS_BASE_URL || "https://careers.edx.org",
    NEXT_PUBLIC_HOME_BASE_URL:
      process.env.NEXT_PUBLIC_HOME_BASE_URL || "https://home.edx.org",
    NEXT_PUBLIC_FEATURE_ENABLE_LOGIN:
      process.env.NEXT_PUBLIC_FEATURE_ENABLE_LOGIN,
    NEXT_PUBLIC_AUTHN_BASE_URL: process.env.NEXT_PUBLIC_AUTHN_BASE_URL,
    NEXT_PUBLIC_MARKETING_EMAILS_OPT_IN:
      process.env.NEXT_PUBLIC_MARKETING_EMAILS_OPT_IN,
    NEXT_PUBLIC_FEATURE_DEGREE_TY_PAGE_ENABLED:
      process.env.NEXT_PUBLIC_FEATURE_DEGREE_TY_PAGE_ENABLED,
    NEXT_PUBLIC_FEATURE_DEGREE_TUITION_MODULE:
      process.env.NEXT_PUBLIC_FEATURE_DEGREE_TUITION_MODULE,
    NEXT_PUBLIC_FEATURE_SEARCH_BAR_REVAMP_TEST:
      process.env.NEXT_PUBLIC_FEATURE_SEARCH_BAR_REVAMP_TEST,
    NEXT_PUBLIC_FEATURE_NAVIGATION_MODAL_TEST:
      process.env.NEXT_PUBLIC_FEATURE_NAVIGATION_MODAL_TEST,
    NEXT_PUBLIC_FEATURE_ABANDONED_CART_TEST:
      process.env.NEXT_PUBLIC_FEATURE_ABANDONED_CART_TEST,
    NEXT_PUBLIC_FEATURE_MULTI_PURCHASE:
      process.env.NEXT_PUBLIC_FEATURE_MULTI_PURCHASE,
    NEXT_PUBLIC_FEATURE_LOB_ENTRY_POINTS:
      process.env.NEXT_PUBLIC_FEATURE_LOB_ENTRY_POINTS,
    NEXT_PUBLIC_FEATURE_STATS_SOCIAL_PROOF_TEST:
      process.env.NEXT_PUBLIC_FEATURE_STATS_SOCIAL_PROOF_TEST,
    NEXT_PUBLIC_FEATURE_OC_COUNTDOWN_BANNER:
      process.env.NEXT_PUBLIC_FEATURE_OC_COUNTDOWN_BANNER,
    NEXT_PUBLIC_FEATURE_CYT_MOBILE_FIRST_REDESIGN:
      process.env.NEXT_PUBLIC_FEATURE_CYT_MOBILE_FIRST_REDESIGN,
    NEXT_PUBLIC_FEATURE_GTM_AUTOMATION_MODULE:
      process.env.NEXT_PUBLIC_FEATURE_GTM_AUTOMATION_MODULE,
    NEXT_PUBLIC_FEATURE_DEGREE_HUB_REDESIGN:
      process.env.NEXT_PUBLIC_FEATURE_DEGREE_HUB_REDESIGN,
    NEXT_PUBLIC_FEATURE_REFERRAL_TEMPLATE:
      process.env.NEXT_PUBLIC_FEATURE_REFERRAL_TEMPLATE,
    NEXT_PUBLIC_FEATURE_AUTHX: process.env.NEXT_PUBLIC_FEATURE_AUTHX,
    NEXT_PUBLIC_DYNAMIC_FLOW_API: process.env.NEXT_PUBLIC_DYNAMIC_FLOW_API,
    NEXT_PUBLIC_AUTHX_TENANT_ID: process.env.NEXT_PUBLIC_AUTHX_TENANT_ID,
    NEXT_PUBLIC_FEATURE_AUTHX_ENTERPRISE_REDIRECT:
      process.env.NEXT_PUBLIC_FEATURE_AUTHX_ENTERPRISE_REDIRECT,
    NEXT_PUBLIC_CHECKOUT_BASE_URL: process.env.NEXT_PUBLIC_CHECKOUT_BASE_URL,
    NEXT_PUBLIC_RECAPTCHA_SITE_KEY: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
    NEXT_PUBLIC_MONARCH_AUTO_REQUEST_ENABLED:
      process.env.NEXT_PUBLIC_MONARCH_AUTO_REQUEST_ENABLED,
    NEXT_PUBLIC_DAPI_COOKIE_NAME: process.env.NEXT_PUBLIC_DAPI_COOKIE_NAME,
    NEXT_PUBLIC_FEATURE_XML_FEEDS_ENABLED:
      process.env.NEXT_PUBLIC_FEATURE_XML_FEEDS_ENABLED,
    NEXT_PUBLIC_FEATURE_SUPERLATIVE_PRODUCT_CARDS:
      process.env.NEXT_PUBLIC_FEATURE_SUPERLATIVE_PRODUCT_CARDS,
    NEXT_PUBLIC_FEATURE_SUBSCRIPTIONS:
      process.env.NEXT_PUBLIC_FEATURE_SUBSCRIPTIONS,
    NEXT_PUBLIC_FEATURE_HOMEPAGE_TRENDING_COURSE_UPDATE:
      process.env.NEXT_PUBLIC_FEATURE_HOMEPAGE_TRENDING_COURSE_UPDATE,
  },
});

export { env, getEnv, getEnvOrFail };
